import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { get, put } from '../api';
import { useMe } from '../auth';
import { Alert, Card, PageHeader, Spinner, Toggle, useAction, useLoader } from '../components/ui';
import type { Settings } from '../types';

const DAY_MINUTES = 24 * 60;
const fmtMinutes = (m: number) => (m % DAY_MINUTES === 0 ? `${m / DAY_MINUTES} days` : m % 60 === 0 ? `${m / 60} hours` : `${m} minutes`);

export function DataRetentionPage() {
  const { isAdmin } = useMe();
  const { data: settings, error, setData } = useLoader(() => get<Settings>('/api/settings'), []);
  const act = useAction();
  const [useMax, setUseMax] = useState(true);
  const [days, setDays] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) {
      setUseMax(settings.retention.setting === null);
      setDays(String(settings.retention.setting ?? settings.retention.max_days));
    }
  }, [settings]);

  const header = <PageHeader title="Data retention" subtitle="How long MsgGuard keeps your messages." />;
  if (!settings) {
    return (
      <>
        {header}
        <Alert>{error}</Alert>
        {!error && <Spinner />}
      </>
    );
  }

  const { max_days } = settings.retention;
  const chosen = Number(days);
  const valid = useMax || (Number.isInteger(chosen) && chosen >= 1 && chosen <= max_days);
  const value = useMax ? null : chosen;
  const dirty = value !== settings.retention.setting;
  const effective = useMax ? max_days : valid ? chosen : settings.retention.days;

  // Context and repeat-offender rules only see messages that are still kept.
  const m = settings.moderation;
  const longestWindow = Math.max(
    m.context.window_minutes,
    ...m.rules.filter((r) => r.enabled).map((r) => r.when.repeat_offender?.window_minutes ?? 0),
  );

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    const res = await act.run('save', () => put<Settings>('/api/settings', { message_retention_days: value }));
    if (res) {
      setData(res);
      setSaved(true);
    }
  };

  return (
    <>
      {header}
      <Alert>{act.error}</Alert>
      <Card title="Messages">
        <form className="form" onSubmit={save}>
          <p>
            Messages, their decisions and their webhook deliveries are deleted <strong>{settings.retention.days} days</strong> after
            MsgGuard receives them. Your plan keeps them for at most {max_days} days.
          </p>
          <Toggle
            checked={useMax}
            disabled={!isAdmin}
            onChange={(v) => {
              setUseMax(v);
              setSaved(false);
            }}
            label={`Keep messages as long as my plan allows (${max_days} days)`}
            hint="If your plan changes, this follows the new plan's maximum."
          />
          {!useMax && (
            <label className="field">
              <span>Days to keep messages</span>
              <input
                type="number"
                min={1}
                max={max_days}
                step={1}
                value={days}
                disabled={!isAdmin}
                onChange={(e) => {
                  setDays(e.target.value);
                  setSaved(false);
                }}
              />
              <span className={`hint ${valid ? '' : 'tone-text-bad'}`}>Between 1 and {max_days}.</span>
            </label>
          )}
          {longestWindow > effective * DAY_MINUTES && (
            <Alert kind="warn">
              Your <Link to="/settings/moderation">moderation rules</Link> look back {fmtMinutes(longestWindow)}, longer than{' '}
              {effective} days. Conversation context and repeat-offender rules only see messages that are still kept.
            </Alert>
          )}
          <ul className="small muted">
            <li>Deleted messages can't be restored. Choosing a longer period later only keeps messages that haven't been deleted yet.</li>
            <li>Server backups can hold deleted messages for up to 14 more days.</li>
            <li>Usage and billing totals are kept separately and aren't affected.</li>
            <li>
              If you resend the <code>message_id</code> of a deleted message, it counts as a new message.
            </li>
          </ul>
          {isAdmin && (
            <div className="btn-row">
              <button className="btn btn-primary" disabled={!dirty || !valid || act.busy === 'save'}>
                {act.busy === 'save' ? 'Saving…' : 'Save'}
              </button>
              {saved && !dirty && <span className="tone-text-ok small">Saved</span>}
            </div>
          )}
        </form>
      </Card>
    </>
  );
}
